import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Eye, EyeOff, Save, MapPin, AlertCircle, CheckCircle2, Percent, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TrackMaintenance() {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingCode, setSavingCode] = useState(null);
  const [drafts, setDrafts] = useState({}); // code -> {display_code, display_name, visible}
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [showHidden, setShowHidden] = useState(false);
  const [naOnly, setNaOnly] = useState(true);
  const [hideInactive, setHideInactive] = useState(true);
  // YYYY-MM-DD cutoff: tracks with no race on/after this are "inactive" (10-day window).
  const inactiveCutoff = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' })
    .format(new Date(Date.now() - 10 * 24 * 60 * 60 * 1000));
  const [expandedCode, setExpandedCode] = useState(null);
  const [toDraft, setToDraft] = useState({}); // pct strings: {winto,dblto,p3to,p4to,p5to}
  const [savingTo, setSavingTo] = useState(false);
  const [expandedPmCode, setExpandedPmCode] = useState(null);
  const [pmDraft, setPmDraft] = useState({}); // dollar strings keyed by pool code
  const [tmDraft, setTmDraft] = useState({}); // ticket-minimum dollar strings (EX/TRI/SPR)
  const [savingPm, setSavingPm] = useState(false);

  const fetchTracks = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const res = await fetch(`${API_URL}/api/feed/tracks`, { credentials: 'include' });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const data = await res.json();
      setTracks(data.tracks || []);
      const d = {};
      (data.tracks || []).forEach((t) => {
        d[t.data_feed_track_code] = {
          display_code: t.display_code || '',
          display_name: t.display_name || '',
          visible: t.visible !== false,
          pool_stream: t.pool_stream === true,
          region: t.region === 'INTL' ? 'INTL' : 'NA',
          has_synthetic: t.has_synthetic === true,
          asp_blocked: t.asp_blocked === true,
          asp_exclude: t.asp_exclude === true,
        };
      });
      setDrafts(d);
    } catch (e) {
      setErr(`Failed to load tracks: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTracks(); }, [fetchTracks]);

  const setDraft = (code, key, value) => {
    setDrafts((prev) => ({ ...prev, [code]: { ...prev[code], [key]: value } }));
  };

  const isDirty = (t) => {
    const d = drafts[t.data_feed_track_code] || {};
    return (
      (d.display_code || '') !== (t.display_code || '') ||
      (d.display_name || '') !== (t.display_name || '') ||
      (d.region || 'NA') !== (t.region === 'INTL' ? 'INTL' : 'NA') ||
      (d.visible !== false) !== (t.visible !== false) ||
      (d.pool_stream === true) !== (t.pool_stream === true) ||
      (d.has_synthetic === true) !== (t.has_synthetic === true) ||
      (d.asp_blocked === true) !== (t.asp_blocked === true) ||
      (d.asp_exclude === true) !== (t.asp_exclude === true)
    );
  };

  const saveTrack = async (code) => {
    setSavingCode(code); setErr(''); setMsg('');
    try {
      const d = drafts[code];
      const res = await fetch(`${API_URL}/api/feed/tracks/${encodeURIComponent(code)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(d),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || `Status ${res.status}`);
      }
      setMsg(`Saved ${code}.`);
      await fetchTracks();
    } catch (e) {
      setErr(`Save failed: ${e.message}`);
    } finally {
      setSavingCode(null);
    }
  };

  const toggleVisible = (code, next) => setDraft(code, 'visible', next);

  const TO_FIELDS = [
    ['winto', 'WIN'],
    ['dblto', 'Daily Double'],
    ['p3to', 'Pick 3'],
    ['p4to', 'Pick 4'],
    ['p5to', 'Pick 5'],
    ['exto', 'Exacta'],
    ['trito', 'Trifecta'],
    ['supto', 'Superfecta'],
    ['omnto', 'Omni'],
    ['p6to', 'Pick 6'],
  ];
  const TO_HORIZONTAL = ['winto', 'dblto', 'p3to', 'p4to', 'p5to'];

  const decToPct = (v) => (v === null || v === undefined || v === '' ? '' : String(+(v * 100).toFixed(2)));
  const pctToDec = (v) => {
    const s = String(v).trim();
    if (s === '') return null;
    const n = parseFloat(s);
    return Number.isNaN(n) ? null : +(n / 100).toFixed(4);
  };

  const openTakeouts = (t) => {
    const code = t.data_feed_track_code;
    if (expandedCode === code) { setExpandedCode(null); return; }
    const draft = {};
    TO_FIELDS.forEach(([k]) => { draft[k] = decToPct(t[k]); });
    setToDraft(draft);
    setExpandedCode(code);
  };

  const saveTakeouts = async (code) => {
    setSavingTo(true); setErr(''); setMsg('');
    try {
      const takeouts = {};
      TO_FIELDS.forEach(([k]) => { takeouts[k] = pctToDec(toDraft[k]); });
      const res = await fetch(`${API_URL}/api/feed/tracks/${encodeURIComponent(code)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ takeouts }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || `Status ${res.status}`);
      }
      setMsg(`Takeouts saved for ${code}.`);
      setExpandedCode(null);
      await fetchTracks();
    } catch (e) {
      setErr(`Takeout save failed: ${e.message}`);
    } finally {
      setSavingTo(false);
    }
  };

  // --- Pool minimums (exotic pools only) — mirrors the takeout editor ---
  const PM_FIELDS = [
    ['EX', 'Exacta'], ['TRI', 'Trifecta'], ['SPR', 'Superfecta'],
    ['DBL', 'Daily Double'], ['P3', 'Pick 3'], ['P4', 'Pick 4'], ['P5', 'Pick 5'], ['P6', 'Pick 6'],
  ];
  const PM_HORIZONTAL = ['DBL', 'P3', 'P4', 'P5', 'P6'];
  const TM_POOLS = ['EX', 'TRI', 'SPR'];
  const tmPlaceholder = (k) => (k === 'EX' ? '2.00' : 'base');

  const openPoolMins = (t) => {
    const code = t.data_feed_track_code;
    if (expandedPmCode === code) { setExpandedPmCode(null); return; }
    const mins = t.pool_minimums || {};
    const draft = {};
    PM_FIELDS.forEach(([k]) => {
      const v = mins[k];
      draft[k] = (v === null || v === undefined || v === '') ? '' : String(v);
    });
    setPmDraft(draft);
    const tm = t.ticket_minimums || {};
    setTmDraft(Object.fromEntries(TM_POOLS.map((k) => [k, tm[k] ? String(tm[k]) : ''])));
    setExpandedCode(null);
    setExpandedPmCode(code);
  };

  const savePoolMins = async (code) => {
    setSavingPm(true); setErr(''); setMsg('');
    try {
      const pool_minimums = {};
      PM_FIELDS.forEach(([k]) => {
        const s = String(pmDraft[k] ?? '').trim();
        if (s !== '') { const n = parseFloat(s); if (!Number.isNaN(n) && n > 0) pool_minimums[k] = n; }
      });
      const res = await fetch(`${API_URL}/api/feed/tracks/${encodeURIComponent(code)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pool_minimums, ticket_minimums: Object.fromEntries(TM_POOLS.map((k) => [k, String(tmDraft[k] ?? '').trim()])) }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || `Status ${res.status}`);
      }
      setMsg(`Pool minimums saved for ${code}.`);
      setExpandedPmCode(null);
      await fetchTracks();
    } catch (e) {
      setErr(`Pool minimum save failed: ${e.message}`);
    } finally {
      setSavingPm(false);
    }
  };

  const poolMinSummary = (t) => {
    const mins = t.pool_minimums || {};
    const filled = PM_FIELDS.filter(([k]) => mins[k] !== null && mins[k] !== undefined && mins[k] !== '').length;
    if (filled === 0) return { label: 'Not set', color: 'bg-slate-700 text-slate-400 border-slate-600' };
    return {
      label: `${filled}/${PM_FIELDS.length}${t.pool_min_admin ? ' · edited' : ' · csv'}`,
      color: filled === PM_FIELDS.length ? 'bg-emerald-600/20 text-emerald-400 border-emerald-600/40'
        : 'bg-amber-500/20 text-amber-400 border-amber-500/40',
    };
  };

  const takeoutSummary = (t) => {
    const filled = TO_FIELDS.filter(([k]) => t[k] !== null && t[k] !== undefined && t[k] !== '').length;
    if (filled === 0) return { label: 'Not set', color: 'bg-slate-700 text-slate-400 border-slate-600' };
    return {
      label: `${filled}/${TO_FIELDS.length}${t.takeout_source === 'csv' ? ' · csv' : t.takeout_source === 'admin' ? ' · edited' : ''}`,
      color: filled === TO_FIELDS.length ? 'bg-emerald-600/20 text-emerald-400 border-emerald-600/40'
        : 'bg-amber-500/20 text-amber-400 border-amber-500/40',
    };
  };

  const visibleTracks = tracks.filter((t) => {
    const d = drafts[t.data_feed_track_code] || {};
    if (!showHidden && d.visible === false) return false;
    // Filter N.A.-only by the SAVED region, not the in-progress draft, so a row
    // being edited (e.g. NA -> Intl) doesn't vanish before the user can Save it.
    const savedRegion = t.region === 'INTL' ? 'INTL' : 'NA';
    if (naOnly && savedRegion !== 'NA') return false;
    // Hide tracks with no races in the last 10 days (keep a row visible while it's
    // being edited so it doesn't vanish mid-change).
    if (hideInactive && !isDirty(t)) {
      const la = t.last_active || '';
      if (!la || la < inactiveCutoff) return false;
    }
    return true;
  });

  return (
    <div className="space-y-6" data-testid="track-maintenance-panel">
      {msg && (
        <div className="bg-emerald-500/10 border border-emerald-500/50 text-emerald-400 px-2 py-3 rounded-lg flex items-center gap-2" data-testid="track-success">
          <CheckCircle2 size={16} /> {msg}
        </div>
      )}
      {err && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-2 py-3 rounded-lg flex items-center gap-2" data-testid="track-error">
          <AlertCircle size={16} /> {err}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1 flex items-center gap-2">
            <MapPin size={20} className="text-emerald-400" /> Track Maintenance
          </h1>
          <p className="text-slate-400 text-sm">
            Override messy feed codes with clean display codes &amp; full names. Hide "pool stream"
            entries (no WIN pool). <span className="text-emerald-400">Display Code</span> is used in compact
            spots; <span className="text-emerald-400">Display Name</span> in headers.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
            <input
              type="checkbox"
              checked={naOnly}
              onChange={(e) => setNaOnly(e.target.checked)}
              className="w-4 h-4 accent-emerald-500"
              data-testid="track-na-only"
            />
            Show N.A. only
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
            <input
              type="checkbox"
              checked={showHidden}
              onChange={(e) => setShowHidden(e.target.checked)}
              className="w-4 h-4 accent-emerald-500"
              data-testid="track-show-hidden"
            />
            Show hidden
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
            <input
              type="checkbox"
              checked={hideInactive}
              onChange={(e) => setHideInactive(e.target.checked)}
              className="w-4 h-4 accent-emerald-500"
              data-testid="track-hide-inactive"
            />
            Hide inactive (10d)
          </label>
          <button
            onClick={fetchTracks}
            className="flex items-center gap-2 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm transition"
            data-testid="track-refresh-btn"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="track-table">
            <thead className="bg-slate-800">
              <tr className="text-left text-slate-300">
                <th className="px-2 py-3 font-semibold leading-tight">Feed<br />Code</th>
                <th className="px-2 py-3 font-semibold">Raw Feed Name</th>
                <th className="px-2 py-3 font-semibold">Poly</th>
                <th className="px-2 py-3 font-semibold leading-tight" title="ASP endpoint can't accept wagers for this track — Auto Tri plays are BLOCKED and manual ADW sends are stopped">ASP<br />Blocked</th>
                <th className="px-2 py-3 font-semibold leading-tight" title="Blanket exclusion — nothing is ever sent to ASP for this track: no auto bets, no manual ADW sends, no emails or log rows">ASP<br />Exclude</th>
                <th className="px-2 py-3 font-semibold">Region</th>
                <th className="px-2 py-3 font-semibold leading-tight">Display<br />Code</th>
                <th className="px-2 py-3 font-semibold">Display Name</th>
                <th className="px-2 py-3 font-semibold">Visible</th>
                <th className="px-2 py-3 font-semibold leading-tight">Pool<br />Stream</th>
                <th className="px-2 py-3 font-semibold">Takeouts</th>
                <th className="px-2 py-3 font-semibold leading-tight">Pool<br />Mins</th>
                <th className="px-2 py-3 font-semibold text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {loading ? (
                <tr><td colSpan="13" className="px-4 py-8 text-center text-slate-400">Loading tracks…</td></tr>
              ) : visibleTracks.length === 0 ? (
                <tr><td colSpan="13" className="px-4 py-8 text-center text-slate-400">No tracks.</td></tr>
              ) : (
                visibleTracks.map((t) => {
                  const code = t.data_feed_track_code;
                  const d = drafts[code] || {};
                  const dirty = isDirty(t);
                  const toSum = takeoutSummary(t);
                  const pmSum = poolMinSummary(t);
                  const isExpanded = expandedCode === code;
                  const isPmExpanded = expandedPmCode === code;
                  return (
                    <React.Fragment key={code}>
                    <tr className="hover:bg-slate-800/40" data-testid={`track-row-${code}`}>
                      <td className="px-2 py-3">
                        <span className="px-2 py-1 text-xs font-mono font-medium rounded bg-slate-700 text-slate-200">{code}</span>
                        {!(t.display_code || '').trim() && (
                          <span
                            className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded bg-amber-500/20 text-amber-400 border border-amber-500/40 align-middle"
                            data-testid={`track-needs-code-${code}`}
                            title="No display code set — handicapping falls back to the raw feed code, which may not match. Set a display code."
                          >
                            <AlertCircle size={11} /> Needs Display Code
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-3 text-slate-300">{t.name || '-'}</td>
                      <td className="px-2 py-3">
                        <input
                          type="checkbox"
                          checked={d.has_synthetic === true}
                          onChange={(e) => setDraft(code, 'has_synthetic', e.target.checked)}
                          className="w-4 h-4 accent-emerald-500 cursor-pointer"
                          data-testid={`track-synthetic-${code}`}
                        />
                      </td>
                      <td className="px-2 py-3">
                        <input
                          type="checkbox"
                          checked={d.asp_blocked === true}
                          onChange={(e) => setDraft(code, 'asp_blocked', e.target.checked)}
                          className="w-4 h-4 accent-rose-500 cursor-pointer"
                          data-testid={`track-aspblocked-${code}`}
                        />
                      </td>
                      <td className="px-2 py-3">
                        <input
                          type="checkbox"
                          checked={d.asp_exclude === true}
                          onChange={(e) => setDraft(code, 'asp_exclude', e.target.checked)}
                          className="w-4 h-4 accent-rose-500 cursor-pointer"
                          data-testid={`track-aspexclude-${code}`}
                        />
                      </td>
                      <td className="px-2 py-3">
                        <select
                          value={d.region || 'NA'}
                          onChange={(e) => setDraft(code, 'region', e.target.value)}
                          className={`px-2 py-1 bg-slate-900 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                            (d.region || 'NA') === 'NA'
                              ? 'border-sky-600/50 text-sky-300'
                              : 'border-purple-600/50 text-purple-300'
                          }`}
                          data-testid={`track-region-${code}`}
                        >
                          <option value="NA">N.A.</option>
                          <option value="INTL">Intl</option>
                        </select>
                      </td>
                      <td className="px-2 py-3">
                        <input
                          type="text"
                          value={d.display_code || ''}
                          onChange={(e) => setDraft(code, 'display_code', e.target.value.toUpperCase())}
                          placeholder={code}
                          maxLength={6}
                          className="w-20 px-2 py-1 bg-slate-900 border border-slate-600 rounded text-white text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          data-testid={`track-display-code-${code}`}
                        />
                      </td>
                      <td className="px-2 py-3">
                        <input
                          type="text"
                          value={d.display_name || ''}
                          onChange={(e) => setDraft(code, 'display_name', e.target.value)}
                          placeholder={t.name}
                          className="w-48 px-2 py-1 bg-slate-900 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          data-testid={`track-display-name-${code}`}
                        />
                      </td>
                      <td className="px-2 py-3">
                        <button
                          onClick={() => toggleVisible(code, !(d.visible !== false))}
                          className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition ${
                            d.visible !== false
                              ? 'bg-emerald-600/20 text-emerald-400 border border-emerald-600/40'
                              : 'bg-slate-700 text-slate-400 border border-slate-600'
                          }`}
                          data-testid={`track-visible-toggle-${code}`}
                        >
                          {d.visible !== false ? <><Eye size={14} /> Visible</> : <><EyeOff size={14} /> Hidden</>}
                        </button>
                      </td>
                      <td className="px-2 py-3">
                        <button
                          onClick={() => setDraft(code, 'pool_stream', !(d.pool_stream === true))}
                          className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition ${
                            d.pool_stream === true
                              ? 'bg-amber-600/20 text-amber-400 border border-amber-600/40'
                              : 'bg-slate-700 text-slate-400 border border-slate-600'
                          }`}
                          data-testid={`track-poolstream-toggle-${code}`}
                          title="Pool-stream tracks are hidden from the race list"
                        >
                          {d.pool_stream === true ? 'Pool Stream' : 'No'}
                        </button>
                      </td>
                      <td className="px-2 py-3">
                        <button
                          onClick={() => openTakeouts(t)}
                          className={`px-2 py-1 rounded text-xs font-medium border transition hover:opacity-80 ${toSum.color}`}
                          data-testid={`track-takeouts-btn-${code}`}
                          title="Edit pool takeouts"
                        >
                          {toSum.label}
                        </button>
                      </td>
                      <td className="px-2 py-3">
                        <button
                          onClick={() => openPoolMins(t)}
                          className={`px-2 py-1 rounded text-xs font-medium border transition hover:opacity-80 ${pmSum.color}`}
                          data-testid={`track-poolmins-btn-${code}`}
                          title="Edit exotic pool minimums"
                        >
                          {pmSum.label}
                        </button>
                      </td>
                      <td className="px-2 py-3 text-right">
                        <button
                          onClick={() => saveTrack(code)}
                          disabled={!dirty || savingCode === code}
                          className="inline-flex items-center gap-1.5 px-3 py-1 text-sm bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed text-white rounded transition"
                          data-testid={`track-save-${code}`}
                        >
                          <Save size={14} /> {savingCode === code ? '…' : 'Save'}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-slate-900/60" data-testid={`track-takeouts-panel-${code}`}>
                        <td colSpan="13" className="px-4 py-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2 text-sm font-semibold text-white">
                              <Percent size={15} className="text-emerald-400" />
                              Pool Takeouts — {d.display_name || t.name} ({code})
                            </div>
                            <button
                              onClick={() => setExpandedCode(null)}
                              className="text-slate-400 hover:text-white"
                              data-testid={`track-takeouts-close-${code}`}
                            >
                              <X size={16} />
                            </button>
                          </div>
                          <p className="text-xs text-slate-400 mb-3">
                            Enter as percentages (e.g. <span className="text-slate-200">17.5</span> for 17.5%). Stored as decimals.
                            Leave blank if unknown. Used by the scoring engine to back out fair-value odds from will-pays.
                          </p>
                          <div className="space-y-3">
                            {[['Horizontal pools', TO_FIELDS.filter(([k]) => TO_HORIZONTAL.includes(k))],
                              ['Vertical pools', TO_FIELDS.filter(([k]) => !TO_HORIZONTAL.includes(k))]].map(([grpLabel, grpFields]) => (
                              <div key={grpLabel}>
                                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1.5">{grpLabel}</div>
                                <div className="flex flex-wrap items-end gap-4">
                                  {grpFields.map(([k, label]) => (
                                    <div key={k} className="flex flex-col">
                                      <label className="text-xs text-slate-400 mb-1">{label}</label>
                                      <div className="relative">
                                        <input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          value={toDraft[k] ?? ''}
                                          onChange={(e) => setToDraft((p) => ({ ...p, [k]: e.target.value }))}
                                          placeholder="—"
                                          className="w-24 pl-2 pr-6 py-1.5 bg-slate-950 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                          data-testid={`track-takeout-${k}-${code}`}
                                        />
                                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 text-xs">%</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                            <button
                              onClick={() => saveTakeouts(code)}
                              disabled={savingTo}
                              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 text-white rounded transition"
                              data-testid={`track-takeouts-save-${code}`}
                            >
                              <Save size={14} /> {savingTo ? 'Saving…' : 'Save Takeouts'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                    {isPmExpanded && (
                      <tr className="bg-slate-900/60" data-testid={`track-poolmins-panel-${code}`}>
                        <td colSpan="13" className="px-4 py-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2 text-sm font-semibold text-white">
                              <Percent size={15} className="text-emerald-400" />
                              Exotic Pool Minimums — {d.display_name || t.name} ({code})
                            </div>
                            <button
                              onClick={() => setExpandedPmCode(null)}
                              className="text-slate-400 hover:text-white"
                              data-testid={`track-poolmins-close-${code}`}
                            >
                              <X size={16} />
                            </button>
                          </div>
                          <p className="text-xs text-slate-400 mb-3">
                            Enter the base bet minimum in <span className="text-slate-200">dollars</span> (e.g. <span className="text-slate-200">1.00</span>, <span className="text-slate-200">0.50</span>, <span className="text-slate-200">0.10</span>).
                            Saved as a curated value — same as the CSV: it's treated as a legit minimum and used as the default when the feed's value is unrecognized. The feed's own legit values are still honored per race. Leave blank to keep the current value. Under Exacta/Trifecta/Superfecta, "tkt $" is the ticket minimum (least a single ticket may cost); blank = default (Exacta $2, others = base).
                            {!(t.display_code || '').trim() && (
                              <span className="block mt-1 text-amber-400">Set a Display Code for this track before editing pool minimums.</span>
                            )}
                          </p>
                          <div className="space-y-3">
                            {[['Vertical pools', PM_FIELDS.filter(([k]) => !PM_HORIZONTAL.includes(k))],
                              ['Horizontal pools', PM_FIELDS.filter(([k]) => PM_HORIZONTAL.includes(k))]].map(([grpLabel, grpFields]) => (
                              <div key={grpLabel}>
                                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1.5">{grpLabel}</div>
                                <div className="flex flex-wrap items-end gap-4">
                                  {grpFields.map(([k, label]) => {
                                    const legit = (t.pool_min_valid || {})[k];
                                    return (
                                    <div key={k} className="flex flex-col">
                                      <label className="text-xs text-slate-400 mb-1">{label} <span className="text-slate-600">({k})</span></label>
                                      <div className="relative">
                                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-xs">$</span>
                                        <input
                                          type="number"
                                          step="0.05"
                                          min="0"
                                          value={pmDraft[k] ?? ''}
                                          onChange={(e) => setPmDraft((p) => ({ ...p, [k]: e.target.value }))}
                                          placeholder="—"
                                          className="w-24 pl-5 pr-2 py-1.5 bg-slate-950 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                          data-testid={`track-poolmin-${k}-${code}`}
                                        />
                                      </div>
                                      {TM_POOLS.includes(k) && (
                                        <div className="relative mt-1">
                                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-[10px]">tkt $</span>
                                          <input
                                            type="number" step="0.05" min="0"
                                            value={tmDraft[k] ?? ''}
                                            onChange={(e) => setTmDraft((p) => ({ ...p, [k]: e.target.value }))}
                                            placeholder={tmPlaceholder(k)}
                                            title="Ticket minimum: least a single ticket may cost. Blank = default (Exacta $2, others = base)"
                                            className="w-24 pl-9 pr-1 py-1 bg-slate-950 border border-slate-700 rounded text-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                            data-testid={`track-ticketmin-${k}-${code}`}
                                          />
                                        </div>
                                      )}
                                      {legit && legit.length > 1 && (
                                        <span className="text-[10px] text-amber-400/80 mt-1" data-testid={`track-poolmin-legit-${k}-${code}`}>
                                          seen: {legit.map((n) => `$${n.toFixed(2)}`).join(', ')}
                                        </span>
                                      )}
                                    </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                            <button
                              onClick={() => savePoolMins(code)}
                              disabled={savingPm}
                              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 text-white rounded transition"
                              data-testid={`track-poolmins-save-${code}`}
                            >
                              <Save size={14} /> {savingPm ? 'Saving…' : 'Save Pool Minimums'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
